import type { MeasurementSessionRecord } from '~/models/measurementSession'
import { buildPhaseRecord, type PhaseRecord } from '~/models/phase'
import { isDesiredOutcome, type ProjectRecord } from '~/models/project'
import type { RouteOutcome } from '~/models/routeRecommendation'
import { isRouteRecommendation } from '~/models/routeRecommendation'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import { recommendRoute } from '~/rules/route/engine'
import { type RouteOutlook, routeOutlook } from '~/rules/route/outlook'
import type { RouteInputSnapshot } from '~/rules/route/snapshot'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import { notifyWritten } from '~/storage/invalidation'
import { getActivePhase, listPhases } from '~/storage/repositories/phaseRepository'
import { getProfile } from '~/storage/repositories/profileRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { getSettings } from '~/storage/repositories/settingsRepository'
import { STORE } from '~/storage/schema'
import { withTransaction } from '~/storage/transactions'
import { type CalendarDate, calendarDateFrom, toIsoTimestamp, unwrapDate } from '~/types/dates'
import { newId } from '~/types/ids'
import type { Centimetres } from '~/types/units'
import { err, ok, type Result } from '~/utils/result'
import { deriveSnapshot } from './measurementService'
import {
  buildRouteSnapshot,
  type ProjectSetupError,
  type SetupMeasurement,
  summariseSetupMeasurement,
} from './projectSetupService'

/**
 * A phase ends into the next one, inside the same project (D-103).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE PROJECT CONTINUES. IT DOES NOT END AND IT DOES NOT FORK.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Until this existed, `createPhase` and `recommendRoute` each had exactly one
 * production caller — project setup — so the engine that decides which phase a
 * user should be in ran once, at first run, and never again. Ending a phase
 * dropped the user into first-run setup, which wrote a **second active project**
 * and orphaned the first.
 *
 * This is the second caller. It creates a phase inside the project that is
 * already running, and nothing here ends a project: that stays the explicit
 * decision in Settings (D-020, D-080).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  NOTHING HERE STARTS ANYTHING ON ITS OWN
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `recommendNextPhase` reads and returns a value. `startNextPhase` is reached
 * only from a button on a screen showing the reasoning. That is the same split
 * setup has, and for the same reason (D-020).
 */

/**
 * Whether the phase-end flow has anything to offer, and what it starts from.
 *
 * `available` is deliberately narrow: a project running, no active phase, and a
 * body-composition phase in the history to succeed. A cardio-only user has never
 * had a phase and is not between phases — offering them a "next phase" would
 * propose body-composition machinery for a domain they did not choose (D-090).
 */
export interface NextPhaseState {
  readonly available: boolean
  readonly project: ProjectRecord | undefined
  /** The phase this one follows. Undefined when there is no history. */
  readonly previousPhase: PhaseRecord | undefined
  /**
   * The earliest day the next phase may start.
   *
   * A phase span is inclusive at both ends, so a handover starts the day *after*
   * the previous phase ended (D-078). This is computed rather than asked, which
   * is why the flow has no start-date field for a user to get wrong.
   */
  readonly earliestStart: CalendarDate | undefined
}

export async function getNextPhaseState(
  db: SolaDatabase,
): Promise<Result<NextPhaseState, StorageError>> {
  const [project, active, all] = await Promise.all([
    getActiveProject(db),
    getActivePhase(db),
    listPhases(db),
  ])
  if (!project.ok) return project
  if (!active.ok) return active
  if (!all.ok) return all

  const previous = [...all.value]
    .filter((phase) => phase.status === 'completed' && phase.actualEndDate !== undefined)
    .sort((left, right) =>
      unwrapDate(right.actualEndDate as CalendarDate).localeCompare(
        unwrapDate(left.actualEndDate as CalendarDate),
      ),
    )[0]

  const unavailable = {
    available: false,
    project: project.value,
    previousPhase: previous,
    earliestStart: undefined,
  } as const

  if (project.value === undefined) return ok(unavailable)
  if (active.value !== undefined) return ok(unavailable)
  if (previous === undefined) return ok(unavailable)

  return ok({
    available: true,
    project: project.value,
    previousPhase: previous,
    earliestStart: dayAfter(previous.actualEndDate as CalendarDate),
  })
}

/**
 * The answers the flow collects, all of them asked afresh (D-103 §4, amended).
 *
 * **The measurement is re-taken because a stale waist routes someone else.**
 * D-071's bands are waist-to-height, moving that number was the whole purpose of
 * the phase that just ended, and the band decides what comes next.
 *
 * **The two training answers are re-asked for exactly the same reason, and that
 * is an amendment to §4 rather than something it said.** Neither `weeklySessions`
 * nor `trainingExperience` is persisted anywhere — setup collects them into a
 * draft it then clears. Without `weeklySessions`,
 * `applyTrainingFrequencyFloor` turns every lean bulk into maintenance, so the
 * re-run would be structurally incapable of recommending the fat-loss-then-lean-
 * bulk transition D-102 and D-103 exist to enable. Six months also genuinely
 * changes how much someone trains.
 */
export interface NextPhaseAnswers {
  readonly waistReadings: readonly string[]
  readonly neckReadings: readonly string[]
  readonly weeklySessions: string
  readonly trainingExperience: string
  /** Pre-filled from the project and editable (D-103 §6). */
  readonly desiredOutcome: string
}

export interface NextPhaseRecommendation {
  readonly outcome: RouteOutcome
  readonly measurement: SetupMeasurement
  readonly snapshot: RouteInputSnapshot
  /** Shown beside the recommendation, stored nowhere (D-102, D-103 §8). */
  readonly outlook: RouteOutlook | undefined
  readonly startDate: CalendarDate
}

/**
 * Run the route engine again, against evidence taken today.
 *
 * The target date is the **project's own**, not a new question: the project is
 * continuing and its horizon has not moved. If that date has already passed, the
 * flow refuses and points at the project decision rather than inventing a new
 * horizon — extending or completing a project is an explicit choice in Settings
 * (D-020, D-080), and quietly picking one here would make it by side effect.
 */
export async function recommendNextPhase(
  db: SolaDatabase,
  answers: NextPhaseAnswers,
  now: Date,
): Promise<Result<NextPhaseRecommendation, ProjectSetupError>> {
  const state = await getNextPhaseState(db)
  if (!state.ok) return err(state.error)
  if (!state.value.available || state.value.project === undefined) {
    return err(
      storageError('invalidRecord', 'There is no finished phase to follow. Nothing was changed.'),
    )
  }

  const project = state.value.project
  const profile = await getProfile(db)
  if (!profile.ok) return err(profile.error)
  if (profile.value === undefined) {
    return err(
      storageError('invalidRecord', 'Project Sola needs your height before it can advise.'),
    )
  }

  const settings = await getSettings(db, now)
  if (!settings.ok) return err(settings.error)

  const heightCm = Number(profile.value.heightCm)
  const measurement = summariseSetupMeasurement(
    answers,
    heightCm,
    settings.value.circumferenceSpreadThresholdCm,
  )
  if (!measurement.ok) return err({ kind: 'setupInvalid', issues: measurement.error })

  /* Today, unless the previous phase ended today or later — in which case the
     day after it, because two phases may not share a day (D-078). Computed, so
     the overlap the create path now refuses is unreachable from this flow. */
  const today = calendarDateFrom(now)
  const earliest = state.value.earliestStart
  const startDate =
    earliest !== undefined && unwrapDate(earliest) > unwrapDate(today) ? earliest : today

  if (unwrapDate(project.targetDate) <= unwrapDate(startDate)) {
    return err({
      kind: 'setupInvalid',
      issues: [
        {
          site: 'session',
          message:
            'This project has already reached its target date, so there is no room for another phase. Extend or complete the project in Settings first.',
        },
      ],
    })
  }

  const snapshot = buildRouteSnapshot(
    {
      desiredOutcome: answers.desiredOutcome,
      targetDate: unwrapDate(project.targetDate),
      weeklySessions: answers.weeklySessions,
      trainingExperience: answers.trainingExperience,
    },
    measurement.value,
    heightCm,
    now,
    /* The reference is the day the phase would start, not the day the form was
       opened. They are the same in the ordinary case and differ when a phase was
       ended with a future date, and the engine's own reference must be the one
       the phase will actually run from.

       Passed here rather than spread over the result afterwards (D-107 §7): the
       override was a second way to set one field, and project setup now needs the
       same thing for a project that starts later. */
    startDate,
  )
  if (snapshot === undefined) {
    return err({
      kind: 'setupInvalid',
      issues: [{ site: 'session', message: 'Choose what you are aiming for next.' }],
    })
  }

  const engineInput: RouteInputSnapshot = snapshot
  const outcome = recommendRoute(engineInput)

  return ok({
    outcome,
    measurement: measurement.value,
    snapshot: engineInput,
    /* Wherever a route recommendation is shown, so is its outlook (D-103 §8).
       Still stored nowhere, still one phase ahead and no further. */
    outlook: isRouteRecommendation(outcome)
      ? routeOutlook({ recommendation: outcome, snapshot: engineInput })
      : undefined,
    startDate,
  })
}

export interface StartNextPhaseInput {
  readonly answers: NextPhaseAnswers
  readonly recommendation: NextPhaseRecommendation
}

/**
 * Create the next phase — the explicit act D-020 requires, one level down.
 *
 * Three writes, together or not at all: the phase, the measurement session it
 * was recommended from, and the destination change if there was one.
 *
 * **`recommendationSnapshot` is deliberately not written.** It is an immutable
 * value copy of what Project Sola advised at setup (D-046); overwriting it would
 * retroactively change the advice the user originally read and agreed to. The
 * phase record is this recommendation's durable trace — it carries the type and
 * the target range, which is what the recommendation decided.
 *
 * **No new `GoalRecord`.** The project's body-composition target is the
 * project's, and it continues with it. A second active goal would be two answers
 * to one question.
 */
export async function startNextPhase(
  db: SolaDatabase,
  input: StartNextPhaseInput,
  now: Date,
): Promise<Result<PhaseRecord, ProjectSetupError>> {
  const { answers, recommendation } = input
  const timestamp = toIsoTimestamp(now)

  if (!isRouteRecommendation(recommendation.outcome)) {
    return err(storageError('invalidRecord', 'There is no route to start. Nothing was changed.'))
  }

  /* Re-read rather than trust what the screen was holding. The flow can be left
     open across a phase being started in another tab, and the guard that made
     the project fork impossible is worth having here too. */
  const state = await getNextPhaseState(db)
  if (!state.ok) return err(state.error)
  if (!state.value.available || state.value.project === undefined) {
    return err(
      storageError('invalidRecord', 'There is no finished phase to follow. Nothing was changed.'),
    )
  }

  const project = state.value.project
  const profile = await getProfile(db)
  if (!profile.ok) return err(profile.error)
  if (profile.value === undefined) {
    return err(
      storageError('invalidRecord', 'Project Sola needs your height before it can advise.'),
    )
  }

  const phase = buildPhaseRecord(
    {
      projectId: project.id,
      type: recommendation.outcome.recommendedPhase,
      startDate: unwrapDate(recommendation.startDate),
      plannedEndDate: unwrapDate(project.targetDate),
      targetWeeklyChangeMinPercent: recommendation.outcome.weeklyTargetPercent.min,
      targetWeeklyChangeMaxPercent: recommendation.outcome.weeklyTargetPercent.max,
    },
    timestamp,
  )
  if (!phase.ok) return err({ kind: 'setupInvalid', issues: phase.error })

  const session: MeasurementSessionRecord = {
    id: newId<'MeasurementSessionId'>(),
    date: recommendation.startDate,
    sites: recommendation.measurement.sites,
    derived: deriveSnapshot(
      recommendation.measurement.sites,
      Number(profile.value.heightCm) as Centimetres,
      undefined,
    ),
    phaseId: phase.value.id,
    recordedAt: timestamp,
    createdAt: timestamp,
    updatedAt: timestamp,
    schemaVersion: DB_SCHEMA_VERSION,
  }

  /* Appended, never overwritten (D-103 §6). `desiredOutcome` stays the current
     one so nothing has to walk the list to read it, and the entry says what it
     replaced so the project's history is a record rather than an argument. */
  const chosen = isDesiredOutcome(answers.desiredOutcome) ? answers.desiredOutcome : undefined
  const destinationChanged = chosen !== undefined && chosen !== project.desiredOutcome
  const updatedProject: ProjectRecord = destinationChanged
    ? {
        ...project,
        desiredOutcome: chosen,
        destinationChanges: [
          ...(project.destinationChanges ?? []),
          {
            changedAt: timestamp,
            ...(project.desiredOutcome === undefined ? {} : { from: project.desiredOutcome }),
            to: chosen,
          },
        ],
        updatedAt: timestamp,
      }
    : project

  const written = await withTransaction(
    db,
    [STORE.phases, STORE.measurementSessions, STORE.projects] as const,
    async (tx) => {
      /* The no-overlap check lives in `createPhase`, which cannot be used inside
         a transaction that also writes the session. Read the spans here instead,
         so the constraint is enforced on this path too rather than assumed. */
      const existing = await tx.objectStore(STORE.phases).getAll()
      const clash = existing.find(
        (other) =>
          unwrapDate(phase.value.startDate) <=
            unwrapDate(other.actualEndDate ?? other.plannedEndDate) &&
          unwrapDate(other.startDate) <= unwrapDate(phase.value.plannedEndDate),
      )
      if (clash !== undefined) {
        throw new Error(
          `Those dates overlap the phase running ${unwrapDate(clash.startDate)} to ${unwrapDate(
            clash.actualEndDate ?? clash.plannedEndDate,
          )}. Phases may not overlap.`,
        )
      }

      await tx.objectStore(STORE.phases).put(phase.value)
      await tx.objectStore(STORE.measurementSessions).put(session)
      if (destinationChanged) {
        await tx.objectStore(STORE.projects).put(updatedProject)
      }
    },
  )
  if (!written.ok) return err(written.error)

  notifyWritten(STORE.phases, STORE.measurementSessions, STORE.projects)
  return ok(phase.value)
}

/** The day after a date, on the calendar. */
function dayAfter(date: CalendarDate): CalendarDate {
  const next = new Date(`${unwrapDate(date)}T00:00:00Z`)
  next.setUTCDate(next.getUTCDate() + 1)
  return calendarDateFrom(next)
}
